import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import {
  ArrowLeft, ArrowRight, Sparkles, Check, Loader2, ShieldOff, Send,
  CalendarOff, Megaphone, MessageSquare,
} from 'lucide-react';
import {
  Eyebrow, RaceBib, Tom, Builder, Crowd, MarginNote, MarkerHighlight, EvidenceCard,
} from '@/components/foundry';
import {
  getCurrentBlueprint,
  acceptBlueprint,
  type KickstartBlueprint,
  type KickstartBlueprintWrapper,
} from '@/services/kickstartBlueprint';
import { createBillingCheckoutSession } from '@/services/api';
import { useAppAuth } from '@/lib/appAuth';

interface Props {
  blueprintId: string;
  onBack: () => void;
}

const WRAPPER_LABEL: Record<KickstartBlueprintWrapper, string> = {
  reward_funnel: 'Reward funnel',
  vsl_application: 'VSL + application',
  checkout_with_bumps: 'Checkout with bumps',
  short_diagnostic: 'AI-native diagnostic',
  sales_call_assisted: 'Application + call',
  no_sales_call: 'No-sales-call bridge',
};

interface ProofTileSpec {
  label: string;
  before: string;
  after: string;
}
interface WrapperProofConfig {
  tiles: [ProofTileSpec, ProofTileSpec, ProofTileSpec];
  caption: string;
}
const WRAPPER_PROOF: Record<KickstartBlueprintWrapper, WrapperProofConfig> = {
  no_sales_call: {
    tiles: [
      { label: 'Sales calls / week', before: '11h', after: '0h' },
      { label: 'Decision speed', before: 'weeks', after: 'hours' },
      { label: 'Replies in week 1', before: '0–2', after: '12+' },
    ],
    caption:
      'PJPT, cohort 03 · numbers from the live build. Not a guarantee — your numbers depend on offer fit, audience, and how fast you ship the bridge.',
  },
  sales_call_assisted: {
    tiles: [
      { label: 'Call time / week', before: '20h unqualified', after: '6h pre-qualified' },
      { label: 'Show rate', before: '~55%', after: '~85%' },
      { label: 'Time on the wrong call', before: 'most weeks', after: 'rarely' },
    ],
    caption:
      'Indicative ranges from the call-assisted pattern · calls stay in the path on purpose. The AI discovery layer filters the booking, not the conversation.',
  },
  vsl_application: {
    tiles: [
      { label: 'VSL watch-through', before: '~25%', after: '~55%' },
      { label: 'Application quality', before: 'noisy', after: 'pre-qualified' },
      { label: 'Time-to-decision', before: 'weeks', after: 'days' },
    ],
    caption:
      'Indicative ranges from the VSL + application pattern · the VSL earns the application, not another talking-head pitch.',
  },
  checkout_with_bumps: {
    tiles: [
      { label: 'Conversion', before: '~1.5%', after: '~3–4%' },
      { label: 'AOV', before: 'baseline', after: '+25–40%' },
      { label: 'Refund rate', before: 'baseline', after: 'flat' },
    ],
    caption:
      'Indicative ranges from the checkout + bumps pattern · decision-ready buyers convert faster on a clean checkout than through a long warm-up.',
  },
  short_diagnostic: {
    tiles: [
      { label: 'Personalization', before: 'generic', after: 'named back' },
      { label: 'Completion', before: '~30%', after: '~70%' },
      { label: 'Reply rate', before: 'baseline', after: '2–3×' },
    ],
    caption:
      'Indicative ranges from the diagnostic pattern · buyers stop feeling like leads when the diagnostic names them back in the language they used.',
  },
  reward_funnel: {
    tiles: [
      { label: 'Engagement', before: 'single ad', after: 'multi-step' },
      { label: 'Opt-in', before: '~20%', after: '~45%' },
      { label: 'Retention', before: 'flat', after: 'reward arc' },
    ],
    caption:
      'Indicative ranges from the reward pattern · the reward mechanic earns the next step so the eventual offer feels earned, not pushed.',
  },
};

type CheckoutTarget = 'plus' | 'core_monthly';
type CheckoutState = 'idle' | 'redirecting' | 'error';

/**
 * BridgePage — post-Kickstart two-offer surface (membership-led 2026-05-06).
 *
 * Locked direction: Plus primary (one payment, includes 12 months Core), Core Monthly
 * secondary downsell. Core Annual is hidden (in-product only). Coaching OS is a future
 * inside-Core upsell, not a peer cold-traffic offer here.
 *
 * Strategy source: docs/plans/2026-05-06-membership-led-offer-strategy.md
 * Handoff: docs/work/2026-05-06-handoff-claude-code-membership-frontend.md
 */
export default function BridgePage({ blueprintId, onBack }: Props) {
  const { getToken } = useAppAuth();
  const [state, setState] = useState<'loading' | 'ready' | 'error' | 'not_found'>('loading');
  const [blueprint, setBlueprint] = useState<KickstartBlueprint | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [checkoutState, setCheckoutState] = useState<CheckoutState>('idle');
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [activeCheckout, setActiveCheckout] = useState<CheckoutTarget | null>(null);
  const offerSectionRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const token = (await getToken?.({ skipCache: true })) ?? null;
        const result = await getCurrentBlueprint(token);
        if (cancelled) return;
        if (!result) {
          setState('not_found');
          return;
        }
        if (String(result._id) !== blueprintId) {
          setState('not_found');
          return;
        }
        setBlueprint(result);
        setState('ready');
      } catch (err) {
        if (cancelled) return;
        setErrorMessage(err instanceof Error ? err.message : 'Could not load your bridge.');
        setState('error');
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [blueprintId, getToken]);

  function scrollToOffers() {
    offerSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  async function handleOpenCheckout(target: CheckoutTarget) {
    if (!blueprint) return;
    if (checkoutState === 'redirecting') return;
    setCheckoutState('redirecting');
    setActiveCheckout(target);
    setCheckoutError(null);
    try {
      // Mark the blueprint accepted before redirecting so any downstream Build Room or
      // Core onboarding seeds from a stable artifact, not a draft. Idempotent.
      const token = (await getToken?.({ skipCache: true })) ?? null;
      await acceptBlueprint(blueprint._id, token);

      const planId: 'acquisition_os_plus' | 'core_monthly' =
        target === 'plus' ? 'acquisition_os_plus' : 'core_monthly';

      const session = await createBillingCheckoutSession({
        planId,
        cadence: target === 'core_monthly' ? 'monthly' : undefined,
        successUrl: `${window.location.origin}/?surface=os&checkout=success&plan=${planId}`,
        cancelUrl: `${window.location.origin}/bridge/${blueprint._id}?checkout=cancelled`,
      });
      if (session?.url) {
        window.location.href = session.url;
      } else {
        throw new Error('Checkout session did not return a URL');
      }
    } catch (err) {
      setCheckoutState('error');
      setCheckoutError(err instanceof Error ? err.message : 'Could not start checkout.');
    }
  }

  if (state === 'loading') {
    return (
      <div className="mx-auto max-w-[1280px] px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid place-items-center gap-3 py-24">
          <Loader2 className="h-5 w-5 animate-spin text-fd-text-muted" />
          <p className="font-geist-mono text-[11px] font-semibold uppercase tracking-[0.10em] text-fd-text-subtle">
            Drafting your bridge…
          </p>
        </div>
      </div>
    );
  }

  if (state === 'not_found') {
    return (
      <div className="mx-auto max-w-[720px] px-4 py-16 sm:px-6 lg:px-8">
        <button
          onClick={onBack}
          className="mb-8 inline-flex items-center gap-1.5 font-geist text-[13px] font-medium text-fd-text-muted transition-colors hover:text-fd-text"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to The Breakthrough
        </button>
        <div
          className="rounded-[14px] border-[1.5px] border-fd-text bg-fd-paper p-8"
          style={{ boxShadow: '5px 5px 0 0 var(--fd-text)' }}
        >
          <Eyebrow className="mb-3">BRIDGE NOT READY</Eyebrow>
          <h1 className="font-geist text-[26px] font-medium tracking-[-0.02em]">
            We can't find a bridge for that link.
          </h1>
          <p className="mt-3 text-[14px] leading-[1.55] text-fd-text-muted">
            Your bridge is drafted from your Lab Work submissions. If you've finished
            them, head back to The Breakthrough and tap "View your bridge" — that recomputes
            it cleanly. If you got this link from an old email, it may have expired (14
            days).
          </p>
          <button
            onClick={onBack}
            className="mt-5 inline-flex h-10 items-center gap-2 rounded-[4px] border-[1.5px] border-fd-text bg-fd-accent px-4 font-geist-mono text-[12px] font-bold uppercase tracking-[0.04em] text-fd-text"
            style={{ boxShadow: '3px 3px 0 0 var(--fd-text)' }}
          >
            Back to The Breakthrough
            <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div className="mx-auto max-w-[720px] px-4 py-16 sm:px-6 lg:px-8">
        <button
          onClick={onBack}
          className="mb-8 inline-flex items-center gap-1.5 font-geist text-[13px] font-medium text-fd-text-muted transition-colors hover:text-fd-text"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to The Breakthrough
        </button>
        <div className="rounded-[14px] border border-fd-coral/40 bg-fd-coral-soft p-6">
          <Eyebrow className="mb-2">BRIDGE ERROR</Eyebrow>
          <p className="font-geist text-[14px] leading-[1.55] text-fd-text">
            {errorMessage ?? 'Something went wrong loading your bridge.'} Refresh the page
            to try again.
          </p>
        </div>
      </div>
    );
  }

  if (!blueprint) return null;

  const wrapperLabel = WRAPPER_LABEL[blueprint.recommended_wrapper];
  const audience = blueprint.buyer_context.audience_one_liner;
  const bottleneck = blueprint.buyer_context.biggest_bottleneck;
  const heroPromise = blueprint.bridge_personalization.hero_promise;
  const proofAngle = blueprint.bridge_personalization.proof_angle;
  const objection = blueprint.bridge_personalization.objection_to_answer;
  const recommendation = blueprint.recommendation;

  const isRedirecting = checkoutState === 'redirecting';

  return (
    <div className="mx-auto max-w-[1280px] px-4 py-7 sm:px-6 lg:px-8">
      {/* Back */}
      <button
        onClick={onBack}
        className="mb-6 inline-flex items-center gap-1.5 font-geist text-[13px] font-medium text-fd-text-muted transition-colors hover:text-fd-text"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        Back to The Breakthrough
      </button>

      {/* HERO — verdict, points to two-offer panel below */}
      <section
        className="relative mb-10 overflow-hidden rounded-[14px] border-[1.5px] border-fd-text bg-fd-paper p-7 sm:p-9"
        style={{ boxShadow: '6px 6px 0 0 var(--fd-text)' }}
      >
        <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-[1.25fr_1fr]">
          <div>
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <RaceBib n="" label="Your AI Discovery Blueprint" tone="race" size="md" shadow="md" />
              <RaceBib n="" label="Drafted from your Lab Work" tone="ink" size="sm" shadow="sm" />
            </div>
            <Eyebrow className="mb-2">YOUR ENGINE · {wrapperLabel.toUpperCase()}</Eyebrow>
            <h1 className="font-geist text-[34px] font-semibold leading-[1.04] tracking-[-0.035em] text-fd-text [text-wrap:balance] sm:text-[42px] lg:text-[50px] lg:leading-[1.0] lg:tracking-[-0.04em]">
              {heroPromise}
            </h1>
            <p className="mt-5 max-w-[560px] text-[15px] leading-[1.55] text-fd-text-muted lg:text-[16px]">
              <b className="text-fd-text">Why this engine, for you:</b>{' '}
              {recommendation.why_this_wrapper}
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <button
                onClick={scrollToOffers}
                className="inline-flex h-12 items-center gap-2 rounded-[4px] border-[1.5px] border-fd-text bg-fd-accent px-5 font-geist-mono text-[14px] font-bold uppercase tracking-[0.04em] text-fd-text transition-all hover:-translate-x-px hover:-translate-y-px"
                style={{ boxShadow: '3px 3px 0 0 var(--fd-text)' }}
              >
                See your two paths
                <ArrowRight className="h-3.5 w-3.5" />
              </button>
              <span className="font-geist-mono text-[11px] font-semibold uppercase tracking-[0.06em] text-fd-text-subtle">
                Build now, or join the operating environment
              </span>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
            className="relative hidden lg:block"
          >
            <div
              className="relative rounded-[12px] border-[1.5px] border-fd-text bg-fd-bg p-5"
              style={{ boxShadow: '4px 4px 0 0 var(--fd-text)' }}
            >
              <Eyebrow className="mb-2">VERDICT · ON THE EASEL</Eyebrow>
              <p className="font-geist text-[18px] font-medium leading-[1.25] tracking-[-0.01em] text-fd-text">
                {recommendation.headline}
              </p>
              <ul className="mt-3 grid gap-1.5 text-[13px] leading-[1.45] text-fd-text-muted">
                {recommendation.first_build_scope.slice(0, 4).map((item) => (
                  <li key={item} className="flex items-start gap-2">
                    <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-fd-accent" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="mt-2 grid grid-cols-[auto_1fr] items-end gap-3">
              <Tom variant="pointing-right" className="h-[180px] w-auto" />
              <Builder
                variant="reading-notebook"
                alt="A coach reading their coral notebook"
                className="h-[180px] w-auto"
              />
            </div>
            <MarginNote
              tilt={-3}
              tone="teal"
              size="sm"
              className="absolute -top-3 right-3 bg-fd-paper px-2 py-0.5 rounded-[3px] border border-fd-text"
            >
              we drafted this from your lab work.
            </MarginNote>
          </motion.div>
        </div>
      </section>

      {/* TWO-COLUMN — proof angle (col-7) + buyer context recap (col-5) */}
      <section className="mb-10 grid grid-cols-12 gap-5">
        <article
          className="col-span-12 rounded-[14px] border-[1.5px] border-fd-text bg-fd-paper p-7 lg:col-span-7"
          style={{ boxShadow: '5px 5px 0 0 var(--fd-text)' }}
        >
          <Eyebrow className="mb-2">WHY THIS WORKS · PROOF ANGLE</Eyebrow>
          <h2 className="font-geist text-[24px] font-medium leading-[1.18] tracking-[-0.015em] sm:text-[26px]">
            {proofAngle}
          </h2>
          <div className="mt-5 grid grid-cols-3 gap-3">
            {WRAPPER_PROOF[blueprint.recommended_wrapper].tiles.map((tile) => (
              <ProofTile
                key={tile.label}
                label={tile.label}
                before={tile.before}
                after={tile.after}
              />
            ))}
          </div>
          <p className="mt-4 text-[12.5px] leading-[1.55] text-fd-text-muted">
            {WRAPPER_PROOF[blueprint.recommended_wrapper].caption}
          </p>
        </article>

        <article
          className="col-span-12 rounded-[14px] border-[1.5px] border-fd-text bg-fd-paper p-7 lg:col-span-5"
          style={{ boxShadow: '5px 5px 0 0 var(--fd-text)' }}
        >
          <Eyebrow className="mb-2">YOUR CONTEXT · FROM YOUR LAB WORK</Eyebrow>
          <dl className="grid gap-3">
            <ContextRow label="What you sell" value={blueprint.buyer_context.what_they_sell} />
            <ContextRow label="Audience" value={audience} />
            <ContextRow label="Biggest bottleneck" value={bottleneck} />
            <ContextRow
              label="Current acquisition"
              value={blueprint.buyer_context.current_acquisition_model}
            />
          </dl>
        </article>
      </section>

      {/* OBJECTION-TO-ANSWER */}
      <section className="mb-10 grid grid-cols-12 gap-5">
        <article
          className="col-span-12 rounded-[14px] border-[1.5px] border-fd-text bg-fd-paper p-7 lg:col-span-8"
          style={{ boxShadow: '5px 5px 0 0 var(--fd-text)' }}
        >
          <Eyebrow className="mb-2">THE QUESTION YOU'RE ASKING</Eyebrow>
          <h2 className="font-geist text-[22px] font-medium leading-[1.2] tracking-[-0.015em]">
            <MarkerHighlight tone="ink">
              <span className="text-fd-bg">{objection}</span>
            </MarkerHighlight>
          </h2>
          <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <DiffColumn
              header="Funnel templates"
              tone="muted"
              items={['Same shell for everyone', 'Generic copy you have to rewrite', 'No connection to your method']}
            />
            <DiffColumn
              header="Your blueprint"
              tone="strong"
              items={[
                `Built on ${audience}`,
                `Wrapper picked for: ${bottleneck.toLowerCase()}`,
                'Hero, proof, objection — drafted from your Lab Work',
              ]}
            />
          </div>
        </article>

        <article
          className="col-span-12 rounded-[14px] border-[1.5px] border-fd-text bg-fd-paper p-7 lg:col-span-4"
          style={{ boxShadow: '5px 5px 0 0 var(--fd-text)' }}
        >
          <div className="mb-3 grid grid-cols-[1fr_auto] items-start gap-3">
            <div>
              <Eyebrow className="mb-2">WHAT WE'RE NOT DOING FIRST</Eyebrow>
              <p className="text-[13px] leading-[1.55] text-fd-text-muted">
                The Build Room ships one working acquisition system before anything
                else. Your blueprint is honest about what waits.
              </p>
            </div>
            <Tom
              variant="listening"
              alt="Tom seated, reading the coach's lab work"
              className="hidden h-[110px] w-auto shrink-0 sm:block"
            />
          </div>
          <ul className="grid gap-1.5 text-[13px] leading-[1.45] text-fd-text">
            {recommendation.not_doing_first.slice(0, 4).map((item) => (
              <li key={item} className="flex items-start gap-2">
                <span className="mt-1.5 h-1.5 w-3 shrink-0 rounded-[1px] bg-fd-text-subtle/40" />
                {item}
              </li>
            ))}
          </ul>
        </article>
      </section>

      {/* PROOF BAND — Crowd, parity with Breakthrough */}
      <section className="relative mb-10 rounded-[14px] border border-dashed border-fd-border-strong/40 bg-fd-paper p-6 sm:p-7">
        <div className="grid grid-cols-1 items-center gap-6 lg:grid-cols-[1fr_auto]">
          <div>
            <Eyebrow className="mb-2">PROOF · COHORT 03</Eyebrow>
            <h2 className="mb-2 font-geist text-[20px] font-medium tracking-[-0.015em]">
              "Three weeks in I had a bridge live for my real offer. Replies came in by
              Sunday."
            </h2>
            <p className="max-w-[560px] text-[13.5px] leading-[1.55] text-fd-text-muted">
              Pamela J · PJPT clinic OS · cohort 03. Same Build Room, different audience.
              Your build runs on your own context — not Pamela's.
            </p>
          </div>
          <div className="relative shrink-0">
            <Crowd variant="cheering" className="h-[110px] w-auto" />
            <MarginNote tilt={-3} tone="teal" size="sm" className="absolute -top-2 -left-2">
              cohort 03 shipped.
            </MarginNote>
          </div>
        </div>
      </section>

      {/* BRIDGE CHATBOT — V1 stub. Real chatbot integration is route-intelligence-driven
          and ships with the Acquisition OS skill pack. This is the surface contract. */}
      <section className="mb-10 grid grid-cols-12 gap-5">
        <article
          className="col-span-12 rounded-[14px] border-[1.5px] border-fd-text bg-fd-paper p-7 lg:col-span-12"
          style={{ boxShadow: '5px 5px 0 0 var(--fd-text)' }}
        >
          <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-[1fr_1.4fr]">
            <div>
              <Eyebrow className="mb-2">BRIDGE CHAT · NO SALES CALL</Eyebrow>
              <h2 className="font-geist text-[22px] font-medium leading-[1.2] tracking-[-0.02em] [text-wrap:balance]">
                Ask anything before you choose your path.
              </h2>
              <p className="mt-3 text-[13.5px] leading-[1.55] text-fd-text-muted">
                The chat already knows your blueprint, your audience, and your bottleneck.
                Use it for fit, scope, timing, or "is this for me" questions. Replies are
                drafted by your AI Co-Founder, escalated to Tom when judgment is needed,
                and async by default — no Calendly link.
              </p>
              <ul className="mt-4 grid gap-1.5 text-[13px] leading-[1.45] text-fd-text">
                <ChatPrompt label='"What does month one actually look like?"' />
                <ChatPrompt label='"Is this right for a low-volume, high-ticket coach?"' />
                <ChatPrompt label='"What if my offer changes mid-build?"' />
              </ul>
            </div>
            <ChatStub
              audienceLine={audience}
              wrapperLabel={wrapperLabel}
            />
          </div>
        </article>
      </section>

      {/* TWO-OFFER PANEL — Plus primary, Core Monthly secondary */}
      <section
        ref={offerSectionRef}
        id="bridge-offers"
        className="mb-10 grid grid-cols-12 gap-5 scroll-mt-6"
      >
        <PlusOfferCard
          loading={isRedirecting && activeCheckout === 'plus'}
          disabled={isRedirecting}
          onChoose={() => handleOpenCheckout('plus')}
        />
        <CoreMonthlyOfferCard
          loading={isRedirecting && activeCheckout === 'core_monthly'}
          disabled={isRedirecting}
          onChoose={() => handleOpenCheckout('core_monthly')}
        />
        {checkoutState === 'error' && checkoutError ? (
          <div
            role="alert"
            className="col-span-12 rounded-[10px] border border-fd-coral/40 bg-fd-coral-soft px-4 py-3 font-geist text-[13px] leading-[1.5] text-fd-text"
          >
            Checkout couldn't open: {checkoutError}. Try once more — your blueprint is saved.
          </div>
        ) : null}
        <p className="col-span-12 font-geist-mono text-[10.5px] uppercase tracking-[0.05em] text-fd-text-subtle">
          One product per row · same buyer, different starting point. Switching paths later
          is supported and credits Core if you upgrade to Plus inside the membership.
        </p>
      </section>

      {/* FOUNDER PRESENCE NOTE — Tom voice, lane statement */}
      <section className="mb-10">
        <article
          className="rounded-[14px] border-[1.5px] border-fd-text bg-fd-bg p-7 sm:p-9"
          style={{ boxShadow: '5px 5px 0 0 var(--fd-text)' }}
        >
          <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-[auto_1fr]">
            <Tom
              variant="listening"
              alt="Tom in conversation pose"
              className="hidden h-[150px] w-auto lg:block"
            />
            <div>
              <Eyebrow className="mb-2">FROM TOM · LANE STATEMENT</Eyebrow>
              <p className="font-geist text-[18px] font-medium leading-[1.4] tracking-[-0.01em] text-fd-text [text-wrap:balance]">
                I'm not positioned as a Meta ads guru or a high-ticket coach of coaches.
                My lane is helping coaches and experts adopt AI as a serious operating
                layer — acquisition, delivery, content, workflows, decisions.
              </p>
              <p className="mt-3 text-[14px] leading-[1.55] text-fd-text-muted">
                Two kids, full-time job — my calendar can't be the product, so it isn't.
                The product is the operating environment, the templates, the AI
                Co-Founder, the bounded build track, and the Lab support around all of it.
                You'll see me at named checkpoints and inside The Lab — async, batched,
                and present where it matters.
              </p>
            </div>
          </div>
        </article>
      </section>

      {/* HONEST EXCLUSIONS — what this is NOT */}
      <section className="mb-10">
        <article
          className="rounded-[14px] border border-dashed border-fd-border-strong/60 bg-fd-paper p-7"
        >
          <Eyebrow className="mb-3">WHAT THIS IS NOT</Eyebrow>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <ExclusionRow
              icon={<ShieldOff className="h-3.5 w-3.5" strokeWidth={1.75} />}
              label="Not done-for-you"
              detail="You own the repo, the agents, the Meta account, and the decisions. We supply method, templates, review."
            />
            <ExclusionRow
              icon={<CalendarOff className="h-3.5 w-3.5" strokeWidth={1.75} />}
              label="No sales calls"
              detail="Bridge chat is async by default. Tom shows up at named checkpoints, not on a Calendly link."
            />
            <ExclusionRow
              icon={<Megaphone className="h-3.5 w-3.5" strokeWidth={1.75} />}
              label="No webinar treadmill"
              detail="Live sessions are productised and replayable. The replay library compounds. No content treadmill."
            />
            <ExclusionRow
              icon={<MessageSquare className="h-3.5 w-3.5" strokeWidth={1.75} />}
              label="Not unlimited coaching"
              detail="Plus reviews are bounded at named checkpoints. Core is method + community + replays. Neither pretends to be 1:1."
            />
            <ExclusionRow
              icon={<Sparkles className="h-3.5 w-3.5" strokeWidth={1.75} />}
              label="Not a guarantee"
              detail="Numbers above are indicative. Your build runs on your context. We promise the work, not the outcome."
            />
            <ExclusionRow
              icon={<Send className="h-3.5 w-3.5" strokeWidth={1.75} />}
              label="Not a fixed funnel template"
              detail="Route intelligence runs first. The Build Track ships the route your business actually needs, not the one we sell."
            />
          </div>
        </article>
      </section>

      {/* SIGNOFF — Tom verdict card */}
      <section className="mb-10 grid grid-cols-12 gap-5">
        <article className="col-span-12 lg:col-span-8 lg:col-start-3">
          <EvidenceCard kind="verdict" eyebrow="TOM'S SIGNOFF · BRIDGE READY">
            <div className="grid grid-cols-[auto_1fr] items-start gap-3">
              <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-fd-accent-deep" />
              <p className="text-[14px] leading-[1.55]">
                I read your Lab Work. The wrapper above is what I'd actually build for you,
                in this order, against the bottleneck you named. If anything in the
                blueprint feels off when we start the Build Room, we adjust — your context
                wins, every time.
              </p>
            </div>
          </EvidenceCard>
        </article>
      </section>
    </div>
  );
}

// ── Offer cards ────────────────────────────────────────────────────────

function PlusOfferCard({
  loading,
  disabled,
  onChoose,
}: {
  loading: boolean;
  disabled: boolean;
  onChoose: () => void;
}) {
  return (
    <article
      className="col-span-12 rounded-[14px] border-[1.5px] border-fd-text bg-fd-paper p-7 lg:col-span-7"
      style={{ boxShadow: '6px 6px 0 0 var(--fd-text)' }}
    >
      <header className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <RaceBib n="" label="PRIMARY · BUILD NOW" tone="race" size="md" shadow="md" />
          <h3 className="mt-3 font-geist text-[28px] font-semibold leading-[1.05] tracking-[-0.025em] text-fd-text">
            AI Acquisition OS Plus
          </h3>
          <p className="mt-1 font-geist text-[14px] leading-[1.45] text-fd-text-muted">
            One payment · 12 months The AI Brand Lab included · bounded Build Track checkpoints
          </p>
        </div>
        <div className="text-right">
          <p className="font-geist text-[36px] font-semibold leading-none tracking-[-0.03em] text-fd-text">
            $1,997
          </p>
          <p className="mt-1 font-geist-mono text-[10px] font-semibold uppercase tracking-[0.06em] text-fd-text-subtle">
            Founding · moves to $2,497 after first cohort
          </p>
        </div>
      </header>

      <ul className="mb-5 grid gap-2 text-[13.5px] leading-[1.5] text-fd-text">
        <Bullet>
          <b>The AI Brand Lab membership</b> for 12 months — The Shift, The Math,
          AI Co-Founder Workspace setup, weekly AI operating briefings, replay library.
        </Bullet>
        <Bullet>
          <b>Acquisition OS Build Track</b> — your AI Co-Founder picks the right route, runs the
          research, builds the funnel, supports your Meta Ads, then reads the signal week to week.
        </Bullet>
        <Bullet>
          <b>Tom + specialist AI review</b> at named build checkpoints. Bounded, async, present where it matters.
        </Bullet>
        <Bullet>
          <b>The Game Plan</b> — interactive workbook for income, conversion, and audience clarity.
          Sets the strategic frame before any build work starts. Your context wins.
        </Bullet>
        <Bullet>
          <b>The Lab support desk</b> with priority queue while the build is running.
        </Bullet>
      </ul>

      <div className="mb-5 grid grid-cols-1 gap-2 rounded-[8px] border border-fd-border bg-fd-bg p-3 sm:grid-cols-3">
        <RefundFact label="Refund window" value="14 days" />
        <RefundFact label="Or until" value="Build Brief accepted" />
        <RefundFact label="Whichever lands first" value="—" />
      </div>

      <button
        type="button"
        onClick={onChoose}
        disabled={disabled}
        className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-[4px] border-[1.5px] border-fd-text bg-fd-accent px-5 font-geist-mono text-[14px] font-bold uppercase tracking-[0.04em] text-[oklch(0.16_0.005_95)] shadow-[3px_3px_0_0_var(--fd-text)] transition-transform hover:-translate-x-px hover:-translate-y-px active:translate-x-0 active:translate-y-0 disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto"
      >
        {loading ? (
          <>
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Opening checkout…
          </>
        ) : (
          <>
            Choose Plus · $1,997
            <ArrowRight className="h-3.5 w-3.5" />
          </>
        )}
      </button>
    </article>
  );
}

function CoreMonthlyOfferCard({
  loading,
  disabled,
  onChoose,
}: {
  loading: boolean;
  disabled: boolean;
  onChoose: () => void;
}) {
  return (
    <article
      className="col-span-12 flex flex-col rounded-[14px] border border-fd-border bg-fd-bg p-7 lg:col-span-5"
    >
      <header className="mb-5">
        <p className="font-geist-mono text-[10.5px] font-semibold uppercase tracking-[0.08em] text-fd-text-subtle">
          NOT READY TO BUILD YET? · CORE MONTHLY
        </p>
        <h3 className="mt-2 font-geist text-[24px] font-semibold leading-[1.1] tracking-[-0.02em] text-fd-text">
          The AI Brand Lab · monthly
        </h3>
        <p className="mt-1 font-geist text-[13.5px] leading-[1.45] text-fd-text-muted">
          Join the operating environment, install foundations, upgrade to the Build Track
          when you're ready.
        </p>
        <p className="mt-3 font-geist text-[28px] font-semibold leading-none tracking-[-0.025em] text-fd-text">
          $99<span className="font-geist-mono text-[14px] font-medium tracking-normal text-fd-text-muted">/mo</span>
        </p>
        <p className="mt-1 font-geist-mono text-[10px] font-semibold uppercase tracking-[0.06em] text-fd-text-subtle">
          Cancel anytime · upgrade credit if you move to Plus
        </p>
      </header>

      <ul className="mb-5 grid gap-2 text-[13px] leading-[1.5] text-fd-text">
        <BulletQuiet>The Shift orientation + The Math reverse-revenue tool.</BulletQuiet>
        <BulletQuiet>AI Co-Founder Workspace setup guide for non-technical Builders.</BulletQuiet>
        <BulletQuiet>Five practical AI-native coach workflows.</BulletQuiet>
        <BulletQuiet>Weekly AI Operating Briefing · adopt / ignore / test.</BulletQuiet>
        <BulletQuiet>The Lab support desk + community + promoted shared answers.</BulletQuiet>
        <BulletQuiet>Replay library — every live session compounds.</BulletQuiet>
      </ul>

      <p className="mb-5 rounded-[8px] border border-dashed border-fd-border bg-fd-paper p-3 text-[12px] leading-[1.5] text-fd-text-muted">
        Core teaches the shared operating layer. The Build Track applies that layer to
        acquisition. Coaching OS later applies it to delivery — unlocked from inside Core
        when you're ready.
      </p>

      <button
        type="button"
        onClick={onChoose}
        disabled={disabled}
        className="mt-auto inline-flex h-11 w-full items-center justify-center gap-2 rounded-[4px] border-[1.5px] border-fd-text bg-fd-paper px-5 font-geist-mono text-[12.5px] font-bold uppercase tracking-[0.04em] text-fd-text transition-transform hover:-translate-y-px active:translate-y-0 disabled:cursor-not-allowed disabled:opacity-70"
      >
        {loading ? (
          <>
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Opening checkout…
          </>
        ) : (
          <>
            Join Core · $99/mo
            <ArrowRight className="h-3.5 w-3.5" />
          </>
        )}
      </button>
    </article>
  );
}

// ── Small composables ─────────────────────────────────────────────────

function ProofTile({ label, before, after }: { label: string; before: string; after: string }) {
  return (
    <div className="rounded-[10px] border border-fd-border bg-fd-bg p-3">
      <div className="mb-1 font-geist-mono text-[9.5px] font-semibold uppercase tracking-[0.08em] text-fd-text-subtle">
        {label}
      </div>
      <div className="flex items-baseline gap-2">
        <span className="font-geist-mono text-[14px] text-fd-text-subtle line-through">{before}</span>
        <ArrowRight className="h-3 w-3 text-fd-text-subtle" />
        <span className="font-geist-mono text-[20px] font-semibold tracking-[-0.02em] text-fd-text">{after}</span>
      </div>
    </div>
  );
}

function ContextRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid gap-1 border-t border-fd-border pt-3 first:border-t-0 first:pt-0">
      <dt className="font-geist-mono text-[10.5px] font-semibold uppercase tracking-[0.06em] text-fd-text-subtle">
        {label}
      </dt>
      <dd className="font-geist text-[14px] leading-[1.45] text-fd-text">{value}</dd>
    </div>
  );
}

function DiffColumn({
  header,
  items,
  tone,
}: {
  header: string;
  items: string[];
  tone: 'muted' | 'strong';
}) {
  const isStrong = tone === 'strong';
  return (
    <div
      className={[
        'rounded-[10px] border p-4',
        isStrong ? 'border-fd-text bg-fd-accent-soft' : 'border-fd-border bg-fd-bg',
      ].join(' ')}
    >
      <div
        className={[
          'mb-2 font-geist-mono text-[10.5px] font-semibold uppercase tracking-[0.08em]',
          isStrong ? 'text-fd-text' : 'text-fd-text-subtle',
        ].join(' ')}
      >
        {header}
      </div>
      <ul className="grid gap-1.5">
        {items.map((item) => (
          <li
            key={item}
            className={[
              'flex items-start gap-2 text-[13px] leading-[1.45]',
              isStrong ? 'text-fd-text' : 'text-fd-text-muted',
            ].join(' ')}
          >
            <Check
              className={[
                'mt-0.5 h-3.5 w-3.5 shrink-0',
                isStrong ? 'text-fd-text' : 'text-fd-text-subtle',
              ].join(' ')}
            />
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
}

function Bullet({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-2.5">
      <Check className="mt-1 h-3.5 w-3.5 shrink-0 text-fd-accent-deep" />
      <span>{children}</span>
    </li>
  );
}

function BulletQuiet({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-2.5">
      <span className="mt-1.5 block h-1 w-1 shrink-0 rounded-full bg-fd-text-subtle" />
      <span>{children}</span>
    </li>
  );
}

function RefundFact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="font-geist-mono text-[9.5px] font-semibold uppercase tracking-[0.08em] text-fd-text-subtle">
        {label}
      </div>
      <div className="mt-0.5 font-geist text-[12.5px] font-medium text-fd-text">{value}</div>
    </div>
  );
}

function ChatPrompt({ label }: { label: string }) {
  return (
    <li className="flex items-start gap-2 rounded-[6px] border border-fd-border bg-fd-bg px-3 py-1.5 italic">
      <MessageSquare className="mt-1 h-3 w-3 shrink-0 text-fd-text-subtle" strokeWidth={1.5} />
      {label}
    </li>
  );
}

function ExclusionRow({
  icon,
  label,
  detail,
}: {
  icon: React.ReactNode;
  label: string;
  detail: string;
}) {
  return (
    <div className="flex items-start gap-3">
      <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-[4px] border border-fd-border bg-fd-bg text-fd-text-muted">
        {icon}
      </span>
      <div>
        <p className="font-geist text-[13.5px] font-semibold leading-[1.3] text-fd-text">{label}</p>
        <p className="mt-0.5 text-[12.5px] leading-[1.5] text-fd-text-muted">{detail}</p>
      </div>
    </div>
  );
}

// V1 chat stub — visual contract for the eventual route-intelligence chatbot. Real chat
// integration ships with the Acquisition OS skill pack. This is the seat the chatbot will
// fill on the bridge page; it is intentionally non-functional for V1 so the surface
// promise is set without overpromising the experience.
function ChatStub({ audienceLine, wrapperLabel }: { audienceLine: string; wrapperLabel: string }) {
  return (
    <div
      className="rounded-[12px] border-[1.5px] border-fd-text bg-fd-bg p-4"
      style={{ boxShadow: '4px 4px 0 0 var(--fd-text)' }}
    >
      <div className="mb-3 flex items-center gap-2 border-b border-fd-border pb-2">
        <span className="block h-2 w-2 rounded-full bg-fd-teal" />
        <span className="font-geist-mono text-[10.5px] font-semibold uppercase tracking-[0.08em] text-fd-text-subtle">
          AI Co-Founder · context loaded
        </span>
      </div>
      <div className="grid gap-2.5">
        <ChatBubble role="ai">
          I have your blueprint. You sell to{' '}
          <span className="font-medium text-fd-text">{audienceLine}</span>, and the
          recommended engine is the{' '}
          <span className="font-medium text-fd-text">{wrapperLabel}</span>. Ask me anything
          before you choose your path.
        </ChatBubble>
        <ChatBubble role="hint">
          Bridge chat goes live with the Acquisition OS skill pack. Until then, ask in The
          Lab once you're inside Core or Plus — your blueprint and context come with you.
        </ChatBubble>
      </div>
      <div className="mt-3 flex items-center gap-2 rounded-[6px] border border-dashed border-fd-border bg-fd-paper px-3 py-2 text-fd-text-subtle">
        <span className="text-[12.5px] italic">Chat input · coming online with Acquisition OS</span>
        <Send className="ml-auto h-3.5 w-3.5" strokeWidth={1.5} />
      </div>
    </div>
  );
}

function ChatBubble({ role, children }: { role: 'ai' | 'hint'; children: React.ReactNode }) {
  if (role === 'hint') {
    return (
      <p className="rounded-[6px] border border-dashed border-fd-border bg-fd-paper px-3 py-2 text-[12px] leading-[1.5] text-fd-text-subtle">
        {children}
      </p>
    );
  }
  return (
    <p className="rounded-[6px] border border-fd-border bg-fd-paper px-3 py-2 text-[13px] leading-[1.5] text-fd-text">
      {children}
    </p>
  );
}
